import type { HostInfo } from '@casa/bridge';

export interface Diagnostics {
    host: HostInfo | null;
    secureContext: boolean;
    videoDecoder: boolean;
    offscreenCanvas: boolean;
    worker: boolean;
    webSocket: boolean;
    userAgent: string;
}

/** Spike item 1b: which of the capabilities the video pipeline needs does this engine offer? */
export function collectDiagnostics(globals: Record<string, unknown>) : Diagnostics {
    const navigator = globals.navigator as { userAgent?: string } | undefined;

    return {
        host: (globals.casaHost as HostInfo | undefined) ?? null,
        secureContext: globals.isSecureContext === true,
        videoDecoder: typeof globals.VideoDecoder === 'function',
        offscreenCanvas: typeof globals.OffscreenCanvas === 'function',
        worker: typeof globals.Worker === 'function',
        webSocket: typeof globals.WebSocket === 'function',
        userAgent: navigator?.userAgent ?? 'unknown',
    };
}

export interface CodecSupport {
    label: string;
    codec: string;
    /** `true`/`false` from `isConfigSupported`, or the error text when the call failed. */
    hardware: boolean | string;
    software: boolean | string;
}

// 1080p configurations; the codec strings carry profile and level.
const CODECS: readonly { label: string; codec: string }[] = [
    { label: 'H.264 Baseline 3.1', codec: 'avc1.42E01F' },
    { label: 'H.264 Main 4.0', codec: 'avc1.4D4028' },
    { label: 'H.264 High 4.0', codec: 'avc1.640028' },
    { label: 'H.265 Main 4.0 (hvc1)', codec: 'hvc1.1.6.L120.90' },
    { label: 'H.265 Main 4.0 (hev1)', codec: 'hev1.1.6.L120.90' },
];

interface VideoDecoderStatic {
    isConfigSupported(config: {
        codec: string;
        codedWidth: number;
        codedHeight: number;
        hardwareAcceleration: 'prefer-hardware' | 'prefer-software';
    }) : Promise<{ supported?: boolean }>;
}

/**
 * Spike item 1b: what `VideoDecoder.isConfigSupported` answers per codec, asked once for a
 * hardware and once for a software decoder. An engine may ignore the hardware hint, so a `true`
 * here is not yet proof of hardware decoding; iteration I2 reads that off a real stream.
 */
export async function probeCodecs(globals: Record<string, unknown>) : Promise<CodecSupport[]> {
    const decoder = globals.VideoDecoder as VideoDecoderStatic | undefined;
    if (typeof decoder?.isConfigSupported !== 'function') {
        return [];
    }

    const ask = async (codec: string, hardwareAcceleration: 'prefer-hardware' | 'prefer-software') => {
        try {
            const answer = await decoder.isConfigSupported({
                codec,
                codedWidth: 1920,
                codedHeight: 1080,
                hardwareAcceleration,
            });

            return answer.supported === true;
        } catch (error) {
            return error instanceof Error ? error.message : String(error);
        }
    };

    return Promise.all(
        CODECS.map(async ({ label, codec }) => ({
            label,
            codec,
            hardware: await ask(codec, 'prefer-hardware'),
            software: await ask(codec, 'prefer-software'),
        })),
    );
}

/** Answer of `public/sandbox/probe-worker.js`. */
export interface WorkerProbe {
    secureContext: boolean;
    videoDecoder: boolean;
    offscreenCanvas: boolean;
    offscreenCanvas2d: boolean;
    /** `blocked (...)` or `allowed (...)`. */
    fetch: string;
    webSocket: string;
    /** `securitypolicyviolation` events seen in the worker. */
    violations: string[];
}

export interface SandboxProbe {
    /** Null when the worker could not be started or did not answer. */
    worker: WorkerProbe | null;
    /** The same fetch from the page, as the control: it must be allowed. */
    pageFetch: string;
    /** True only when the page may fetch the URL and the worker may not. */
    sandboxed: boolean;
    error?: string;
}

/**
 * Spike item 1b: starts a worker from `/sandbox/`, which the shells serve with
 * `Content-Security-Policy: connect-src 'none'`, and checks that it cannot reach the network.
 * The Vite dev server sends no such header, so there the result is "not sandboxed".
 */
export async function probeSandbox(timeoutMs = 8000) : Promise<SandboxProbe> {
    const fetchUrl = location.href;
    let pageFetch: string;
    try {
        pageFetch = `allowed (status ${(await fetch(fetchUrl)).status})`;
    } catch (error) {
        pageFetch = `blocked (${String(error)})`;
    }

    let worker: Worker;
    try {
        worker = new Worker(new URL('sandbox/probe-worker.js', document.baseURI));
    } catch (error) {
        return { worker: null, pageFetch, sandboxed: false, error: String(error) };
    }

    try {
        const answer = await new Promise<WorkerProbe>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('worker did not answer')), timeoutMs);
            worker.onmessage = (event) => {
                clearTimeout(timer);
                resolve(event.data as WorkerProbe);
            };
            worker.onerror = (event) => {
                clearTimeout(timer);
                reject(new Error(`worker failed to load: ${event.message || 'unknown error'}`));
            };
            // Port 9 (discard) on loopback: nothing listens there, the attempt itself is what counts.
            worker.postMessage({ fetchUrl, wsUrl: 'wss://127.0.0.1:9/' });
        });

        return {
            worker: answer,
            pageFetch,
            sandboxed: pageFetch.startsWith('allowed') && answer.fetch.startsWith('blocked'),
        };
    } catch (error) {
        return { worker: null, pageFetch, sandboxed: false, error: String(error) };
    } finally {
        worker.terminate();
    }
}
